import React, { useRef, useState, useEffect, useContext } from "react";
import { timeStringToSeconds } from "../../lib/common";
import { Context } from "../contexts/AppContext";

function VideoThumbnailExtractor(props) {
  const videoRef = useRef(null);
  const [thumbnail, setThumbnail] = useState(null);
  const { value } = useContext(Context);
  const [appState, setAppState] = value;
  const [src, setSrc] = useState("");

  // 🔹 Set initial video source
  useEffect(() => {
    if (props?.videodata?.url) {
      setSrc(
        `${process.env.REACT_APP_VIDEO_DEST_BASE_PATH || ""}${props.videodata.url.trim()}`
      );
    }
  }, [props?.videodata?.url]);

  // 🔹 Load cached thumbnail if exists
  useEffect(() => {
    if (props?.videodata?._id) {    
      
  setTimeout(() => {
  const cached = localStorage.getItem(`thumb_${props.videodata._id}`);
  if (cached) {
    setThumbnail(cached);
  }
}, 300);

    }
  }, [props?.videodata?._id]);

  // 🔹 Fallback if main URL fails
  const handleError = () => {
    console.warn("Primary video path failed. Switching to fallback...");
    setSrc(
      `${process.env.REACT_APP_VIDEO_TEST_BASE_PATH || ""}${props?.videodata?.url?.trim() || ""}`
    );
  };

  // 🔹 FIFO thumbnail cache (avoid full localStorage)
  const saveThumbnailWithQuota = (id, dataUrl) => {
    try {
      localStorage.setItem(id, dataUrl);
    } catch (e) {
      if (e.name === "QuotaExceededError" || e.code === 22) {
        console.warn("localStorage full, deleting oldest thumbnail...");

        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key.startsWith("thumb_")) keys.push(key);
        }

        if (keys.length > 0) {
          localStorage.removeItem(keys[0]);
          console.log("Removed old thumbnail:", keys[0]);
        }

        // Retry saving
        try {
          localStorage.setItem(id, dataUrl);
        } catch (err) {
          console.error("Still cannot save thumbnail:", err);
        }
      } else {
        console.error("localStorage error:", e);
      }
    }
  };

  // 🔹 Generate thumbnail once video metadata and seek are ready
  useEffect(() => {
    const video = videoRef.current;
    if (!video || thumbnail) return;

    const handleLoadedMetadata = () => {
      const cue = props?.videodata?.cuePoint
        ? timeStringToSeconds(props.videodata.cuePoint)
        : 10;

      // Prevent seeking beyond duration
      const safeTime = Math.min(cue, video.duration - 0.1);
      video.currentTime = safeTime;
    };

    const handleSeeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const maxWidth = 440;
        const aspect = video.videoHeight / video.videoWidth;

        canvas.width = maxWidth;
        canvas.height = maxWidth * aspect;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const captured = canvas.toDataURL("image/jpeg", 0.6);
        setThumbnail(captured);
        

        if (props?.videodata?._id) {
          saveThumbnailWithQuota(`thumb_${props.videodata._id}`, captured);
        }
      } catch (err) {
        console.error("Thumbnail capture failed:", err);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true });
    video.addEventListener("seeked", handleSeeked, { once: true });

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("seeked", handleSeeked);
    };
  }, [props?.videodata, thumbnail]);

return (
  <div className="absolute inset-0 overflow-hidden rounded-[20px]">
    <video
      ref={videoRef}
      style={{
        position: "absolute",
        left: "-9999px",
        top: "-9999px",
        width: "1px",
        height: "1px",
      }}
      src={src.replace(/([^:]\/)\/+/g, "$1")}
      preload="auto"
      crossOrigin="anonymous"
      playsInline
      muted
      onError={handleError}
    />

    {thumbnail && (
      <img
        className="w-full h-full object-cover rounded-[20px]"
        id="thumbnailImage"
        src={thumbnail}
        alt="Video Thumbnail"
      />
    )}
  </div>
);

}

export default VideoThumbnailExtractor;
